import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Building2, Plus, Trash2, Loader2, ChevronRight, ChevronDown, Layers } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  subsidiary: { color: "#3B82F6", label: "Subsidiary" },
  venture: { color: "#22C55E", label: "Venture" },
  department: { color: "#D4AF37", label: "Department" },
  joint_venture: { color: "#A855F7", label: "Joint Venture" },
  project_co: { color: "#F59E0B", label: "Project Co" },
  fund: { color: "#06B6D4", label: "Fund" },
  spv: { color: "#EC4899", label: "SPV" },
  branch: { color: "#84CC16", label: "Branch" },
};

export default function Entities() {
  const [entities, setEntities] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [portfolio, setPortfolio] = useState({});
  const blank = { name: "", type: "subsidiary", parent_id: "", description: "", country: "Ghana", currency: "GHS", status: "active" };
  const [newEnt, setNewEnt] = useState(blank);

  const load = () => {
    setLoading(true);
    api.get("/entities").then((r) => {
      setEntities(r.data.entities);
      setCounts(r.data.counts || {});
    }).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const tree = useMemo(() => {
    const byParent = {};
    for (const e of entities) {
      const key = e.parent_id || "__root__";
      (byParent[key] ||= []).push(e);
    }
    return byParent;
  }, [entities]);

  const create = async () => {
    if (!newEnt.name.trim()) return;
    try {
      const payload = { ...newEnt, parent_id: newEnt.parent_id || null };
      await api.post("/entities", payload);
      setNewEnt(blank);
      setCreating(false);
      load();
      toast.success("Entity created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this entity? Children will be re-parented to the root.")) return;
    try {
      await api.delete(`/entities/${id}`);
      load();
    } catch { toast.error("Delete failed"); }
  };

  const toggle = async (id) => {
    setExpanded((p) => ({ ...p, [id]: !p[id] }));
    if (!portfolio[id]) {
      try {
        const r = await api.get(`/entities/${id}/portfolio`);
        setPortfolio((p) => ({ ...p, [id]: r.data }));
      } catch {/* ignore */}
    }
  };

  const renderNode = (e, depth = 0) => {
    const children = tree[e.id] || [];
    const isOpen = expanded[e.id];
    const meta = TYPE_META[e.type] || { color: "#94A3B8", label: e.type };
    return (
      <div key={e.id}>
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 p-3 border border-white/5 hover:border-white/15 hover:bg-white/[0.02] transition-all"
          style={{ marginLeft: depth * 24 }}
          data-testid={`entity-${e.id}`}
        >
          <button onClick={() => toggle(e.id)} className="text-slate-500 hover:text-white p-1">
            {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
          <div className="w-1 h-8 shrink-0" style={{ background: meta.color }} />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-white truncate">{e.name}</div>
            <div className="text-[11px] text-slate-500">{meta.label} · {e.country || "—"} · {e.currency || "GHS"}</div>
          </div>
          <span className="font-mono text-[9px] tracking-widest" style={{ color: meta.color }}>{e.status?.toUpperCase()}</span>
          <button onClick={() => remove(e.id)} data-testid={`entity-delete-${e.id}`} className="text-slate-600 hover:text-red-400 p-1">
            <Trash2 className="w-3 h-3" />
          </button>
        </motion.div>
        {isOpen && portfolio[e.id] && (
          <div className="ml-12 mb-2 p-3 border-l-2 border-white/5 bg-white/[0.01]">
            <div className="grid grid-cols-3 gap-3 text-[12px]">
              <div><span className="text-slate-500">Projects:</span> <span className="text-white">{portfolio[e.id].summary.project_count}</span></div>
              <div><span className="text-slate-500">Proposals:</span> <span className="text-white">{portfolio[e.id].summary.proposal_count}</span></div>
              <div><span className="text-slate-500">Proposal value:</span> <span className="text-[#D4AF37]">GH₵ {(portfolio[e.id].summary.proposal_value_ghs || 0).toLocaleString()}</span></div>
            </div>
            {e.description && <p className="text-[12px] text-slate-400 mt-2">{e.description}</p>}
          </div>
        )}
        {isOpen && children.map((c) => renderNode(c, depth + 1))}
      </div>
    );
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="entities-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-1">// Multi-Entity Infrastructure</div>
          <h1 className="font-display text-3xl text-white tracking-tight">Entities</h1>
          <p className="text-slate-400 text-sm mt-1 max-w-2xl">
            Manage subsidiaries, ventures, departments, joint ventures and SPVs as a hierarchical portfolio. Roll up
            proposals, projects, and finance per entity.
          </p>
        </div>
        <button onClick={() => setCreating(!creating)} data-testid="new-entity-btn" className="text-[11px] font-mono tracking-widest px-3 py-2 flex items-center gap-1.5" style={{ background: "var(--gold-500)", color: "#050B14" }}>
          <Plus className="w-3 h-3" /> NEW ENTITY
        </button>
      </div>

      {/* Counts strip */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(counts.by_type || {}).map(([t, c]) => {
          const meta = TYPE_META[t] || { color: "#94A3B8", label: t };
          return (
            <div key={t} className="px-3 py-1.5 border border-white/10 flex items-center gap-2" data-testid={`count-${t}`}>
              <span className="w-2 h-2" style={{ background: meta.color }} />
              <span className="font-mono text-[10px] tracking-widest text-slate-300">{meta.label.toUpperCase()}</span>
              <span className="font-mono text-[10px] text-slate-500">{c}</span>
            </div>
          );
        })}
      </div>

      {creating && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="nexus-glass-panel p-5 space-y-3" data-testid="create-entity-form">
          <div className="label-eyebrow">// New entity</div>
          <input value={newEnt.name} onChange={(e) => setNewEnt({ ...newEnt, name: e.target.value })} placeholder="Entity name" data-testid="entity-name" className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select value={newEnt.type} onChange={(e) => setNewEnt({ ...newEnt, type: e.target.value })} data-testid="entity-type" className="bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none">
              {Object.entries(TYPE_META).map(([t, m]) => <option key={t} value={t}>{m.label}</option>)}
            </select>
            <select value={newEnt.parent_id} onChange={(e) => setNewEnt({ ...newEnt, parent_id: e.target.value })} className="bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none">
              <option value="">— top-level (no parent) —</option>
              {entities.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <input value={newEnt.country} onChange={(e) => setNewEnt({ ...newEnt, country: e.target.value })} placeholder="Country" className="bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
          </div>
          <textarea value={newEnt.description} onChange={(e) => setNewEnt({ ...newEnt, description: e.target.value })} rows={2} placeholder="Description" className="w-full bg-[#0A111F] border border-white/10 text-white text-sm p-2.5 resize-none focus:border-[#D4AF37]/60 focus:outline-none" />
          <button onClick={create} data-testid="save-entity" className="text-[11px] font-mono tracking-widest px-3 py-2" style={{ background: "var(--gold-500)", color: "#050B14" }}>CREATE ENTITY</button>
        </motion.div>
      )}

      {loading ? (
        <div className="p-10 text-slate-400 font-mono text-xs flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> LOADING…</div>
      ) : entities.length === 0 ? (
        <div className="nexus-glass-panel p-10 text-center" data-testid="entities-empty">
          <Layers className="w-8 h-8 mx-auto text-slate-600 mb-3" strokeWidth={1.5} />
          <h3 className="font-display text-lg text-white">No entities yet</h3>
          <p className="text-sm text-slate-400 mt-1">Create your first subsidiary, venture or department to begin organizing your portfolio.</p>
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="entity-tree">
          {(tree["__root__"] || []).map((e) => renderNode(e, 0))}
        </div>
      )}
    </div>
  );
}
